import { useState } from 'react';
import Layout from '@/components/Layout';

interface FechaFiscal {
  id: string;
  titulo: string;
  fecha: Date;
  tipo: 'iva' | 'irpf' | 'sociedades' | 'modelo' | 'otro';
  descripcion: string;
  completado: boolean;
}

export default function ClienteCalendario() {
  const [fechasFiscales] = useState<FechaFiscal[]>([
    {
      id: '1',
      titulo: 'IVA 4º Trimestre 2024',
      fecha: new Date('2025-01-30'),
      tipo: 'iva',
      descripcion: 'Presentación del IVA del último trimestre de 2024',
      completado: true,
    },
    {
      id: '2',
      titulo: 'IRPF 1º Trimestre 2025',
      fecha: new Date('2025-04-20'),
      tipo: 'irpf',
      descripcion: 'Pago fraccionado del IRPF correspondiente al primer trimestre',
      completado: false,
    },
    {
      id: '3',
      titulo: 'IVA 1º Trimestre 2025',
      fecha: new Date('2025-04-20'),
      tipo: 'iva',
      descripcion: 'Presentación del IVA del primer trimestre de 2025',
      completado: false,
    },
    {
      id: '4',
      titulo: 'Modelo 111 - Retenciones Marzo',
      fecha: new Date('2025-04-20'),
      tipo: 'modelo',
      descripcion: 'Retenciones e ingresos a cuenta de rendimientos del trabajo',
      completado: false,
    },
    {
      id: '5',
      titulo: 'Declaración de la Renta 2024',
      fecha: new Date('2025-06-30'),
      tipo: 'irpf',
      descripcion: 'Presentación de la declaración anual del IRPF',
      completado: false,
    },
  ]);

  const [mesMostrado, setMesMostrado] = useState(new Date());

  const getTipoColor = (tipo: FechaFiscal['tipo']) => {
    switch (tipo) {
      case 'iva':
        return 'bg-calm-bg text-calm-accent border-calm-border';
      case 'irpf':
        return 'bg-calm-bg text-calm-text border-calm-border';
      case 'sociedades':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'modelo':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-calm-bg text-calm-muted border-calm-border';
    }
  };

  const getTipoIcon = (tipo: FechaFiscal['tipo']) => {
    switch (tipo) {
      case 'iva':
        return <span className="material-symbols-outlined text-sm align-middle">bar_chart</span>;
      case 'irpf':
        return <span className="material-symbols-outlined text-sm align-middle">payments</span>;
      case 'sociedades':
        return <span className="material-symbols-outlined text-sm align-middle">business</span>;
      case 'modelo':
        return <span className="material-symbols-outlined text-sm align-middle">list_alt</span>;
      default:
        return <span className="material-symbols-outlined text-sm align-middle">calendar_today</span>;
    }
  };

  const fechasProximas = fechasFiscales
    .filter(f => !f.completado && f.fecha >= new Date())
    .sort((a, b) => a.fecha.getTime() - b.fecha.getTime())
    .slice(0, 5);

  const fechasCompletadas = fechasFiscales.filter(f => f.completado).length;

  return (
    <Layout rol="cliente">
      <div className="h-[calc(100vh-72px)] overflow-y-auto space-y-6 p-4 md:p-8">
        <div>
          <h1 className="text-5xl font-light font-display text-calm-text">Calendario Fiscal</h1>
          <p className="text-calm-muted mt-1">Fechas importantes y recordatorios</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-white to-fresh-sky-50/20 border border-fresh-sky-200/40 rounded-huge p-8">
            <div className="text-sm text-calm-muted">Próximas Obligaciones</div>
            <div className="text-2xl font-light text-calm-text mt-1">{fechasProximas.length}</div>
          </div>
          <div className="bg-gradient-to-br from-white to-emerald-50/20 border border-emerald-200/40 rounded-huge p-8">
            <div className="text-sm text-calm-muted">Completadas este Año</div>
            <div className="text-2xl font-light text-success mt-1">{fechasCompletadas}</div>
          </div>
          <div className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge p-8">
            <div className="text-sm text-calm-muted">Total Obligaciones</div>
            <div className="text-2xl font-light text-calm-text mt-1">{fechasFiscales.length}</div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-white to-fresh-sky-50/20 border border-fresh-sky-200/40 rounded-huge p-8">
          <div className="flex items-start space-x-3">
            <div className="w-10 h-10 rounded-xl bg-fresh-sky-50 flex items-center justify-center text-fresh-sky-500 flex-shrink-0">
              <span className="material-symbols-outlined text-2xl">calendar_today</span>
            </div>
            <div>
              <h3 className="font-light text-calm-text mb-1">No te preocupes</h3>
              <p className="text-sm text-calm-muted">
                Tu asesor se encarga de todas las fechas importantes y te avisará con antelación.
                Este calendario es solo para que estés informado.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge p-8">
          <h3 className="text-lg font-light text-calm-text mb-4">Próximas Fechas Importantes</h3>
          <div className="space-y-3">
            {fechasProximas.map(fecha => {
              const diasRestantes = Math.ceil(
                (fecha.fecha.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
              );
              
              return (
                <div
                  key={fecha.id}
                  className={`border-2 rounded-huge p-4 ${getTipoColor(fecha.tipo)}`}
                >
                  <div className="flex items-start space-x-3">
                    <div className="text-3xl">{getTipoIcon(fecha.tipo)}</div>
                    <div className="flex-1">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-light text-calm-text">{fecha.titulo}</h4>
                          <p className="text-sm text-calm-muted mt-1">{fecha.descripcion}</p>
                        </div>
                        <div className="text-right">
                          <div className="font-light text-calm-text">
                            {fecha.fecha.toLocaleDateString('es-ES', { 
                              day: 'numeric', 
                              month: 'short',
                              year: 'numeric' 
                            })}
                          </div>
                          <div className={`text-xs mt-1 font-medium ${
                            diasRestantes <= 7 ? 'text-red-600' :
                            diasRestantes <= 30 ? 'text-yellow-600' :
                            'text-calm-muted'
                          }`}>
                            {diasRestantes <= 0 ? 'Hoy' :
                             diasRestantes === 1 ? 'Mañana' :
                             `En ${diasRestantes} días`}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {fechasProximas.length === 0 && (
              <div className="text-center py-12">
                <div className="w-16 h-16 rounded-2xl bg-fresh-sky-50 flex items-center justify-center text-fresh-sky-400 mb-4 mx-auto">
                  <span className="material-symbols-outlined text-4xl">check_circle</span>
                </div>
                <h3 className="text-xl font-light text-calm-text mb-2">
                  ¡Todo al día!
                </h3>
                <p className="text-calm-muted">
                  No tienes obligaciones fiscales pendientes próximamente
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="bg-gradient-to-br from-white to-emerald-50/20 border border-emerald-200/40 rounded-huge p-8">
          <h3 className="text-lg font-light text-calm-text mb-4">Obligaciones Completadas</h3>
          <div className="space-y-2">
            {fechasFiscales
              .filter(f => f.completado)
              .map(fecha => (
                <div
                  key={fecha.id}
                  className="flex items-center justify-between p-3 bg-calm-bg rounded-huge"
                >
                  <div className="flex items-center space-x-3">
                    <span className="material-symbols-outlined text-2xl text-green-600">check_circle</span>
                    <div>
                      <div className="font-medium text-calm-text">{fecha.titulo}</div>
                      <div className="text-sm text-calm-muted">
                        {fecha.fecha.toLocaleDateString('es-ES')}
                      </div>
                    </div>
                  </div>
                  <span className="text-xs text-green-600 font-medium">Completado</span>
                </div>
              ))}

            {fechasCompletadas === 0 && (
              <div className="text-center py-8 text-calm-muted text-sm">
                No hay obligaciones completadas todavía
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
